import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useMatch } from 'react-router-dom';
import { Search, Plus, AlertCircle, CheckCircle, XCircle, Users, Loader2, Flag, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Platform } from '../../types/platform';
import { AddChatbotModal } from './AddChatbotModal';

interface AccountSidebarProps {
  activePlatform: string;
  /** Platforms are fetched by App.tsx and passed down — sidebar does NOT fetch independently. */
  platforms: Platform[];
  isLoading?: boolean;
  onRefresh?: () => void;
}

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  zalo: 'Zalo',
  instagram: 'Instagram',
};

const STATUS_ICON: Record<Platform['status'], React.ReactNode> = {
  active:   <CheckCircle size={12} className="text-green-500" />,
  inactive: <XCircle    size={12} className="text-gray-400" />,
  error:    <AlertCircle size={12} className="text-red-500" />,
};

const TOKEN_BADGE: Record<Platform['access_token_status'], { labelKey: string; cls: string }> = {
  valid:   { labelKey: 'connected',    cls: 'bg-green-100 text-green-700' },
  expired: { labelKey: 'tokenExpired', cls: 'bg-red-100 text-red-600' },
  missing: { labelKey: 'notConnected', cls: 'bg-gray-100 text-gray-500' },
};

/** Browser accounts use polling status instead of token status */
const BROWSER_BADGE: Record<string, { labelKey: string; cls: string }> = {
  active:   { labelKey: 'browserActive',   cls: 'bg-green-100 text-green-700' },
  inactive: { labelKey: 'browserInactive', cls: 'bg-amber-100 text-amber-600' },
};

function formatFollowers(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

export const AccountSidebar: React.FC<AccountSidebarProps> = ({
  activePlatform,
  platforms,
  isLoading = false,
  onRefresh,
}) => {
  const { t } = useTranslation('chatbot');
  const navigate = useNavigate();
  const match = useMatch('/chatbot/:id/*');
  const activeAccountId = match?.params.id ?? null;
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // Filter by the active platform type
  const accounts = useMemo(
    () =>
      platforms.filter(
        (p) => !activePlatform || p.platform === activePlatform || p.platform.startsWith(activePlatform),
      ),
    [platforms, activePlatform],
  );

  const filtered = useMemo(
    () =>
      accounts.filter(
        (acc) =>
          acc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          acc.platform_id.includes(searchQuery)
      ),
    [accounts, searchQuery]
  );

  const platformLabel = PLATFORM_LABELS[activePlatform] ?? 'All Platforms';

  return (
    <div className="w-[280px] h-full flex flex-col bg-white border-r border-gray-200 flex-shrink-0">
      {/* Header */}
      <div className="px-3 pt-3 pb-2 border-b border-gray-100 flex flex-col gap-2">
        {/* Title row */}
        <div className="flex items-center justify-between text-[15px] font-bold text-[#1c1c1e]">
          <span>{platformLabel}</span>
          <span className="text-xs font-semibold bg-gray-100 text-gray-500 rounded-[10px] px-2 py-0.5">
            {accounts.length}
          </span>
        </div>

        {/* Search */}
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8e9295] pointer-events-none"
          />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full py-1.5 pl-[30px] pr-2.5 border border-gray-200 rounded-lg text-[13px] outline-none bg-gray-50 focus:border-[#3390ec] focus:bg-white box-border"
          />
        </div>

        {/* Add button */}
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center justify-center gap-1.5 w-full py-2 px-2.5 bg-[#3390ec] text-white border-none rounded-lg text-[13px] font-semibold cursor-pointer hover:bg-[#2b7fd4] transition-colors duration-150"
        >
          <Plus size={15} />
          <span>{t('addChatbot')}</span>
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-300">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-5 text-center text-[#1c1c1e]">
            <div className="text-[36px] mb-2">🤖</div>
            <div className="font-semibold mb-1">{t('noChatbotsFound')}</div>
            <div className="text-[13px] text-[#8e9295]">
              {searchQuery ? t('tryDifferentKeyword') : t('addFirstChatbot', { platform: platformLabel })}
            </div>
          </div>
        ) : (
          filtered.map((account) => {
            const isActive = account.id === activeAccountId;
            const isBrowser = account.platform.endsWith('_browser');
            const badge = isBrowser
              ? BROWSER_BADGE[account.polling_enabled ? 'active' : 'inactive']
              : TOKEN_BADGE[account.access_token_status];
            return (
              <div
                key={account.id}
                onClick={() => navigate(`/chatbot/${account.id}`)}
                className={`flex items-start gap-2.5 px-3 py-2.5 cursor-pointer transition-colors duration-100 border-b border-gray-100 ${
                  isActive
                    ? 'bg-[#e8f2fd] hover:bg-[#dceefb]'
                    : 'hover:bg-[#f5f7fa]'
                }`}
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <img
                    src={account.avatar}
                    alt={account.name}
                    className="w-[42px] h-[42px] rounded-xl object-cover bg-gray-200"
                  />
                  {/* Platform type badge: fanpage vs personal */}
                  {account.platform === 'facebook' && (
                    <span className="absolute -top-1 -left-1 bg-blue-500 rounded-full flex items-center justify-center w-[18px] h-[18px] border-[1.5px] border-white" title="Fanpage">
                      <Flag size={10} className="text-white" />
                    </span>
                  )}
                  {account.platform === 'facebook_browser' && (
                    <span className="absolute -top-1 -left-1 bg-indigo-500 rounded-full flex items-center justify-center w-[18px] h-[18px] border-[1.5px] border-white" title="Tài khoản cá nhân">
                      <User size={10} className="text-white" />
                    </span>
                  )}
                  <span className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full flex items-center justify-center w-4 h-4 border-[1.5px] border-white">
                    {STATUS_ICON[account.status]}
                  </span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-1.5">
                    <span className="text-[13px] font-semibold text-[#1c1c1e] truncate">
                      {account.name}
                    </span>
                    {account.unread_count > 0 && (
                      <span className="flex-shrink-0 bg-[#3390ec] text-white text-[10px] font-bold rounded-lg px-1.5 py-0.5 min-w-[18px] text-center">
                        {account.unread_count > 99 ? '99+' : account.unread_count}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1 text-[11px] text-[#8e9295]">
                    <Users size={11} className="flex-shrink-0" />
                    <span>{t('followers', { count: formatFollowers(account.followers) })}</span>
                    <span className="opacity-50">·</span>
                    <span>{formatDistanceToNow(new Date(account.last_activity), { addSuffix: true })}</span>
                  </div>

                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md inline-block w-fit ${badge.cls}`}>
                    {t(badge.labelKey)}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add Chatbot Modal */}
      <AddChatbotModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        platform={activePlatform}
        onSuccess={(newAcc) => {
          if (onRefresh) onRefresh();
          navigate(`/chatbot/${newAcc.id}`);
        }}
      />
    </div>
  );
};
